import { z } from 'zod';
import { ALERT_CRITICALITIES } from './alerts';
import { DEPARTMENT_TYPES } from './areas';
import { INITIATIVE_STATUSES } from './initiatives';
import { TRAFFIC_LIGHTS } from './status';

/**
 * Dashboard por área (T-143, RF-36, RN-24; DEC-057): o que está sob a responsabilidade de quem vê (o escopo de área),
 * com filtros de área, unidade e responsável — objetivos, KPIs com as metas do mês, iniciativas, ações e pendências.
 */

/** Itens de cada lista (os piores e os mais antigos primeiro). */
export const AREA_LIST_LIMIT = 20;

export const areaDashboardQuerySchema = z
  .object({
    departmentId: z.string().uuid('Área inválida.').optional(),
    businessUnitId: z.string().uuid('Unidade inválida.').optional(),
    responsibleId: z.string().uuid('Responsável inválido.').optional(),
    year: z.coerce.number().int().min(2000).max(2100).optional(),
    month: z.coerce.number().int().min(1).max(12).optional(),
  })
  .refine((v) => (v.year === undefined) === (v.month === undefined), {
    message: 'Informe o ano e o mês juntos.',
    path: ['month'],
  });

const light = z.enum(TRAFFIC_LIGHTS);
const ref = z.object({ id: z.string().uuid(), name: z.string() });
const list = <T extends z.ZodTypeAny>(item: T) =>
  z.object({ total: z.number().int(), items: z.array(item) }).nullable();
const byLight = z.object({
  total: z.number().int(),
  green: z.number().int(),
  yellow: z.number().int(),
  red: z.number().int(),
  /** Sem dado no mês. */
  gray: z.number().int(),
});

export const areaDashboardSchema = z.object({
  project: z.object({
    id: z.string().uuid(),
    name: z.string(),
    horizonStart: z.number().int(),
    horizonEnd: z.number().int(),
  }),
  year: z.number().int(),
  month: z.number().int(),
  computedAt: z.string().nullable(),
  /** Os filtros aplicados (null = sem esse filtro). */
  filters: z.object({
    departmentId: z.string().uuid().nullable(),
    businessUnitId: z.string().uuid().nullable(),
    responsibleId: z.string().uuid().nullable(),
  }),
  /** Opções dos filtros, dentro do escopo de quem vê. */
  options: z.object({
    departments: z.array(
      ref.extend({ type: z.enum(DEPARTMENT_TYPES), parentId: z.string().uuid().nullable() }),
    ),
    businessUnits: z.array(ref),
    people: z.array(ref),
  }),
  summary: z.object({
    objectives: byLight.nullable(),
    kpis: byLight.nullable(),
    initiatives: z.object({ open: z.number().int(), late: z.number().int() }).nullable(),
    actions: z.object({ open: z.number().int(), late: z.number().int() }).nullable(),
    /** Ações atrasadas + resultados não lançados + alertas abertos + decisões pendentes (os visíveis). */
    pending: z.number().int(),
  }),
  objectives: list(
    z.object({
      id: z.string().uuid(),
      name: z.string(),
      perspective: z.string(),
      department: z.string().nullable(),
      owner: z.string().nullable(),
      performance: z.number().nullable(),
      light,
    }),
  ),
  /** KPIs com as metas do mês: meta, realizado e acumulado. */
  kpis: list(
    z.object({
      id: z.string().uuid(),
      code: z.string(),
      name: z.string(),
      department: z.string(),
      owner: z.string(),
      /** Unidade da meta e do realizado (null = o KPI consolidado). */
      unit: ref.nullable(),
      unitLabel: z.string(),
      target: z.number().nullable(),
      actual: z.number().nullable(),
      performance: z.number().nullable(),
      light,
      ytdTarget: z.number().nullable(),
      ytdActual: z.number().nullable(),
      ytdPerformance: z.number().nullable(),
    }),
  ),
  initiatives: list(
    z.object({
      id: z.string().uuid(),
      name: z.string(),
      objective: z.string(),
      leader: z.string().nullable(),
      status: z.enum(INITIATIVE_STATUSES),
      endDate: z.string().nullable(),
      late: z.boolean(),
    }),
  ),
  actions: list(
    z.object({
      id: z.string().uuid(),
      title: z.string(),
      planId: z.string().uuid(),
      planName: z.string(),
      responsibleName: z.string().nullable(),
      dueDate: z.string().nullable(),
      status: z.string(),
      late: z.boolean(),
    }),
  ),
  pending: z.object({
    lateActions: z.number().int().nullable(),
    /** KPIs (ou KPI × unidade) com meta em meses encerrados do ano, até o período, sem realizado lançado. */
    missingResults: list(
      z.object({
        kpiId: z.string().uuid(),
        code: z.string(),
        name: z.string(),
        owner: z.string(),
        unit: z.string().nullable(),
        months: z.array(z.number().int()),
      }),
    ),
    openAlerts: list(
      z.object({
        id: z.string().uuid(),
        title: z.string(),
        criticality: z.enum(ALERT_CRITICALITIES),
        createdAt: z.string(),
      }),
    ),
    pendingDecisions: list(
      z.object({
        id: z.string().uuid(),
        text: z.string(),
        dueDate: z.string().nullable(),
        late: z.boolean(),
        meeting: z.object({ id: z.string().uuid(), title: z.string() }),
      }),
    ),
  }),
});
export type AreaDashboard = z.infer<typeof areaDashboardSchema>;
